import React, {memo, useCallback, useRef, useState} from 'react';
import {CategoryNavigationItems} from './CategoryNavigationItems/CategoryNavigationItems';
import {NavigationFlyoutContentWrapper} from './NavigationFlyoutContent/NavigationFlyoutContentWrapper';

import {Flyout} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {useOnClickOutside} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import {NavigationFlyoutContents} from './NavigationFlyoutContent/NavigationFlyoutContents';
import {NAVIGATION_HEIGHT} from '../constants';
import {useCloseFlyoutContentOnRouteChange} from './hooks/useCloseFlyoutContentOnRouteChange';
import {useSearchBarOpenedState} from '../SearchBar/hooks/useSearchBarExpander';
import {GetHeaderBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {RowSectionStyled} from '../RowSectionStyled';
import {SearchSuggestions} from '../SearchBar/SearchSuggestions';
import {useIsHintCovered} from '../../../features/ShopHint/hooks/useIsHintCovered';

export type NavigationItemsType = Omit<Flyout, 'containers'> | undefined;

export type TouchOrMouseEventHandler<Element = HTMLElement> =
  React.EventHandler<React.MouseEvent<Element> | React.TouchEvent<Element>>;

const TopCategoryNavigationView: React.FC<GetHeaderBulkResponse['navBar']> = ({
  items,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const {activeNavItemIndex, openFlyout, closeFlyout} = useActiveFlyout();
  useCloseFlyoutContentOnRouteChange(closeFlyout);

  useOnClickOutside(wrapperRef, closeFlyout);

  const onEnterNavigationItem = useCallback(
    (index: number) => () => openFlyout(index),
    [openFlyout],
  );

  const {isSearchBarExpanded, expandSearchBar, collapseSearchBar} =
    useSearchBarOpenedState();

  return (
    <TopCategoryNavigationViewWrapper
      ref={wrapperRef}
      onMouseLeave={closeFlyout}
      data-testid="headerNavigation"
    >
      <Wrapper>
        <CategoryNavigationItems
          activeNavItemIndex={activeNavItemIndex}
          items={items}
          onEnterNavigationItem={onEnterNavigationItem}
          isSearchBarExpanded={isSearchBarExpanded}
        />
        <SearchSuggestions
          isSearchBarExpanded={isSearchBarExpanded}
          expandSearchBar={expandSearchBar}
          collapseSearchBar={collapseSearchBar}
        />
      </Wrapper>

      <NavigationFlyoutContentWrapper
        isOpen={
          activeNavItemIndex !== undefined &&
          !!items[activeNavItemIndex]?.flyout
        }
      >
        <NavigationFlyoutContents
          activeNavItemIndex={activeNavItemIndex}
          items={items}
        />
      </NavigationFlyoutContentWrapper>
    </TopCategoryNavigationViewWrapper>
  );
};

const TopCategoryNavigationViewMemo = memo(TopCategoryNavigationView);

export {TopCategoryNavigationViewMemo as TopCategoryNavigationView};

const useActiveFlyout = () => {
  const [activeNavItemIndex, dispatcher] = useState<undefined | number>(
    undefined,
  );
  const {setIsHintCovered} = useIsHintCovered();

  const openFlyout = useCallback((value: number) => {
    dispatcher(value);
    setIsHintCovered(true);
  }, []);
  const closeFlyout = useCallback(() => {
    dispatcher(undefined);
    setIsHintCovered(false);
  }, []);

  return {
    activeNavItemIndex,
    openFlyout,
    closeFlyout,
  };
};

const TopCategoryNavigationViewWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${NAVIGATION_HEIGHT}px;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
`;

const Wrapper = styled(RowSectionStyled)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;
